import { Box, Flex, Tag } from '@chakra-ui/react';
import React from 'react';

import type { FieldItem } from '../../config';
import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import { renderPlainText } from '../../slots/renderPlainText';
import { BAR_BG, BAR_BORDER, PATIENT_BAR_H, SHELL_PAD } from '../../styles/tokens';
import { Slot } from '../Slot';

/** Ascending by `order`, dropping any field marked `visible:false` -- the ordering/visibility
 *  contract every `FieldItem`-driven container in this folder follows. `.filter` on a `readonly
 *  FieldItem[]` already returns a fresh mutable array, so `.sort` never mutates the config's own
 *  (readonly, `as const`) field list. */
function visibleFieldsInOrder(fields: readonly FieldItem[]): FieldItem[] {
  return fields
    .filter((field) => field.visible)
    .sort((a, b) => a.order - b.order);
}

/**
 * Middle header bar -- patient/encounter summary + case actions (full population is a later
 * story). When `header.patientSummary.fields` is `[]` this renders an empty, labelled
 * `role="region"` container; when a config supplies fields (the default seeds the PatientInfo and
 * CaseSpecialty info slots plus the HoldAction action slot, config/defaultConfig.ts), each renders in
 * host chrome selected by `field.presentation` (see below).
 *
 * `header.patientSummary.visible` gates the CONTAINER itself, same geometry-preserving hide
 * contract as `TopBarContainer`: `visible:false` sets `visibility:hidden` + `aria-hidden`, never
 * `display:none`, so fixed header geometry never collapses; `data-content-key` stays present in
 * both states so tests/future wiring can always find the container.
 *
 * Each field renders in host chrome selected by `field.presentation` (CDMX-18412): the default
 * (`'tag'`/absent) wraps the slot in the reused Chakra `Tag` (`data-testid="field-tag"`), proving the
 * atom-reuse requirement; `'plain'` renders a bare positioning `Box` (`data-testid="field-slot"`)
 * with no Tag padding/bg/radius, so an action slot (e.g. HoldAction's icon button) isn't boxed in a
 * pill. The field's body is a `Slot` reading `field.contentId`'s bound value out of injected `state`
 * -- when `field.componentId` resolves in the registry the component renders in place of plain text,
 * otherwise the fallback (`renderPlainText(value) ?? field.label`) reproduces the original behavior:
 * bound value as text, or `field.label` when there's no bound value.
 *
 * The bar itself is plain white (`BAR_BG`). The INFO (`'tag'`) fields -- e.g. the default PatientInfo
 * and CaseSpecialty slots -- live TOGETHER inside a single grouped chip (`data-testid=
 * "patient-summary-info"`): a soft, bordered, `inline-flex` pill (the lavender-grey this bar itself
 * used to carry) so the two read as one grouped unit against the white bar. Adjacent info fields
 * inside that chip are separated by a thin, `aria-hidden` vertical rule (drawn before every info
 * field except the first). Action slots (`'plain'`, e.g. HoldAction) render OUTSIDE the chip and
 * group to the bar's right edge (first action takes `marginLeft:auto`), so a right-grouped action
 * button is never boxed into the info chip and never gets a stray leading divider.
 */
export const PatientSummaryBar: React.FC = () => {
  const { config, state } = useCodingScreen();
  const { mode } = config;
  const { contentKey, visible, fields } = config.header.patientSummary;
  const ordered = visibleFieldsInOrder(fields);
  // INFO (`'tag'`) fields share one grouped chip; action (`'plain'`) slots render outside it and
  // group to the bar's right edge.
  const infoFields = ordered.filter((f) => f.presentation !== 'plain');
  const actionFields = ordered.filter((f) => f.presentation === 'plain');

  const renderSlot = (field: FieldItem): React.ReactElement => {
    const value = readState(state, field.contentId);
    return (
      <Slot
        item={field}
        value={value}
        mode={mode}
        fallback={() => renderPlainText(value) ?? field.label}
      />
    );
  };

  return (
    <Flex
      role="region"
      aria-label="Patient summary"
      data-content-key={contentKey}
      aria-hidden={visible ? undefined : true}
      align="center"
      gap={2}
      px={SHELL_PAD}
      bg={BAR_BG}
      borderBottom={`1px solid ${BAR_BORDER}`}
      style={{
        height: PATIENT_BAR_H,
        minHeight: PATIENT_BAR_H,
        visibility: visible ? 'visible' : 'hidden'
      }}
    >
      {infoFields.length > 0 && (
        // The grouped INFO chip: `inline-flex` pill carrying the soft lavender-grey/hairline this bar
        // itself used to have, so PatientInfo + CaseSpecialty read as one unit on the now-white bar.
        // `flexShrink:1` + `minWidth:0` let it give up width (and its children ellipsize) before the
        // right-grouped actions do. Each info field keeps its reused Chakra `Tag` host
        // (`data-testid="field-tag"`, `data-slot-key`) -- the chip supplies the visual chrome, so the
        // Tag itself is padding/background-free.
        <Flex
          data-testid="patient-summary-info"
          display="inline-flex"
          align="center"
          gap="6px"
          px="11px"
          py="5px"
          borderRadius="6px"
          bg="var(--panel-2, #e6e3eb)"
          border="1px solid var(--border, #d5d1de)"
          fontSize="12px"
          color="var(--text-2, #5b5966)"
          flexShrink={1}
          minW={0}
          overflow="visible"
          whiteSpace="nowrap"
        >
          {infoFields.map((field, index) => (
            // A thin vertical rule separates two adjacent info fields (before every field except the
            // first). Purely decorative chrome (`aria-hidden`), never a field/host, so it never
            // appears in the accessibility tree or a `field-tag` query; `flexShrink={0}` keeps its
            // 1px width from collapsing when the chip is tight.
            <React.Fragment key={field.contentKey}>
              {index > 0 && (
                <Box
                  as="span"
                  aria-hidden
                  data-testid="patient-summary-divider"
                  flexShrink={0}
                  w="1px"
                  h="13px"
                  mx="2px"
                  bg="var(--border-strong, #c9c8ce)"
                />
              )}
              <Tag
                data-slot-key={field.contentKey}
                data-testid="field-tag"
                bg="transparent"
                px={0}
                py={0}
                minH="auto"
              >
                {renderSlot(field)}
              </Tag>
            </React.Fragment>
          ))}
        </Flex>
      )}
      {actionFields.map((field, index) => (
        // Action slots group to the RIGHT edge of the bar: the first one takes `marginLeft:auto`,
        // consuming the free space after the info chip, which pushes it and every trailing action
        // right. Rendered bare (no Tag chrome) so an icon button isn't boxed into a pill.
        <Box
          key={field.contentKey}
          data-slot-key={field.contentKey}
          data-testid="field-slot"
          display="flex"
          alignItems="center"
          marginLeft={index === 0 ? 'auto' : undefined}
        >
          {renderSlot(field)}
        </Box>
      ))}
    </Flex>
  );
};
